import { feathers } from '@/feathers-client';
import { PreferenceKeys } from '@/Core/config/preferences';
import { ALL_VITALS } from '@/Core/config/vitals';

// figure out what mutation to use based on the daily vitals endpoint that was called
const getDailyVitalsMutation = vitalsEndpoint => {
  switch (vitalsEndpoint) {
    case 'count-events':
      return 'setEventCounts';
    case 'count-contacts':
      return 'setContactCounts';
    case 'recent-contacts':
      return 'setRecentContacts';
    case 'recent-events':
      return 'setRecentEvents';
    default:
      return 'vitalsError';
  }
};

/**
 * Controls daily vitals widget on dashboard.
 */
export default {
  namespaced: true,
  state: {
    vitalsList: null,
    recentEvents: [],
    recentContacts: [],
    contactCounts: {},
    eventCounts: {},
    vitalsError: false,
    vitalsErrorMessage: '',
    vitalsInProgress: false,
    vitalsInProgressCount: 0,
  },
  getters: {
    eventCountsByType(state) {
      const { eventCounts } = state;
      const formatted = {};
      if (!eventCounts || !eventCounts.this_period) {
        return formatted;
      }

      const eventTypes = Object.keys(eventCounts.this_period);
      eventTypes.forEach(eventType => {
        ALL_VITALS.forEach(vital => {
          let typeName;
          if (vital.type === eventType) {
            typeName = eventType;
          } else if (vital.aliases && vital.aliases.length && vital.aliases.includes(eventType)) {
            typeName = vital.type;
          }

          if (typeName) {
            if (formatted[typeName]) {
              formatted[typeName].this_period += eventCounts.this_period[eventType];
              formatted[typeName].last_period += eventCounts.last_period[eventType];
            } else {
              formatted[typeName] = {
                this_period: eventCounts.this_period[eventType],
                last_period: eventCounts.last_period[eventType],
              };
            }
          }
        });
      });
      return formatted;
    },
    mergedEvents(state) {
      const { recentEvents } = state;
      const mergedEvents = [];
      if (recentEvents && recentEvents.length) {
        ALL_VITALS.forEach(vital => {
          recentEvents.forEach(recentEvent => {
            if (vital.type === recentEvent.type) {
              mergedEvents.push(recentEvent);
            } else if (
              vital.aliases &&
              vital.aliases.length &&
              vital.aliases.includes(recentEvent.type)
            ) {
              const aliasEvent = { ...recentEvent };
              aliasEvent.type = vital.type;
              mergedEvents.push(aliasEvent);
            }
          });
        });
      }
      return mergedEvents;
    },
  },
  mutations: {
    vitalsInProgress(state) {
      state.vitalsInProgressCount ++;
      state.vitalsInProgress = true;
      state.vitalsError = false;
    },
    setRecentEvents(state, payload) {
      state.recentEvents = payload.data.events;
      state.vitalsError = false;
      state.vitalsInProgressCount --;
      if (state.vitalsInProgressCount === 0) {
        state.vitalsInProgress = false;
      }
    },
    setRecentContacts(state, payload) {
      state.recentContacts = payload.data.contacts;
      state.vitalsError = false;
      state.vitalsInProgress = false;
    },
    setContactCounts(state, payload) {
      state.contactCounts = payload.data;
      state.vitalsError = false;
      state.vitalsInProgressCount --;
      if (state.vitalsInProgressCount === 0) {
        state.vitalsInProgress = false;
      }
    },
    setEventCounts(state, payload) {
      state.eventCounts = payload.data;
      state.vitalsError = false;
      state.vitalsInProgressCount --;
      if (state.vitalsInProgressCount === 0) {
        state.vitalsInProgress = false;
      }
    },
    vitalsError(state, payload) {
      state.vitalsError = true;
      state.vitalsInProgressCount --;
      if (state.vitalsInProgressCount === 0) {
        state.vitalsInProgress = false;
      }
      state.vitalsErrorMessage = `Daily Vitals: ${payload.code} ${payload.message}`;
    },
    clearErrors(state, payload) {
      state.vitalsError = false;
      state.vitalsError = payload;
    },
  },
  actions: {
    async getVitals({ commit, rootState }, { vitalsRange, vitalsEndpoints }) {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - vitalsRange);
      startDate.setHours(0, 0, 0, 0);

      vitalsEndpoints.forEach(async endpoint => {
        commit('vitalsInProgress');
        const query = {
          period_days: vitalsRange,
        };

        const dbOrderedVitals = rootState.userPreferences[PreferenceKeys.OrderedVitals].map(vital =>
          Object.assign({}, vital));

        let eventVitals;
        if (dbOrderedVitals) {
          ALL_VITALS.forEach(vital => {
            dbOrderedVitals.forEach(dbVital => {
              if (vital.type === dbVital.type) {
                dbVital.aliases = vital.aliases;
              }
            });
            dbOrderedVitals.push(vital);
          });

          eventVitals = dbOrderedVitals.filter(vital => vital.type !== 'contacts_new');
        } else {
          eventVitals = ALL_VITALS.filter(vital => vital.type !== 'contacts_new');
        }
        if (endpoint === 'count-events' || endpoint === 'recent-events') {
          const activeVitals = eventVitals.filter(vitalConfig => vitalConfig.visible);
          const activeVitalTypes = [];
          activeVitals.forEach(activeVital => {
            activeVitalTypes.push(activeVital.type);
            if (activeVital.aliases) {
              activeVitalTypes.push(...activeVital.aliases);
            }
          });
          query.event_types = activeVitalTypes;
        }
        try {
          const response = await feathers
            .service(`reporting/daily-vitals/${endpoint}`)
            .find({ query });
          const mutation = getDailyVitalsMutation(endpoint);
          commit(mutation, response);
        } catch (error) {
          return commit('vitalsError', error);
        }
      });
    },
    clearErrors({ commit }) {
      commit('clearErrors');
    },
    getVitalsCounters({ dispatch }, vitalsRange) {
      const vitalsEndpoints = ['count-events', 'count-contacts'];
      const payload = { vitalsRange, vitalsEndpoints }
      dispatch('getVitals', payload);
    },
    getVitalsRecentInfo({ dispatch }, vitalsRange) {
      const vitalsEndpoints = ['recent-events', 'recent-contacts'];
      const payload = { vitalsRange, vitalsEndpoints }
      dispatch('getVitals', payload);
    },
    async getVitalTypeRecentInfo({ commit }, payload) {
      commit('vitalsInProgress');
      const {range, eventType} = payload;

      let endpoint = 'recent-events';
      if (eventType === 'contacts_new') {
        endpoint = 'recent-contacts';
      }

      let eventTypes = [eventType];
      const eventVital = ALL_VITALS.find(vital => vital.type === eventType);
      if (eventVital && eventVital.aliases) {
        eventTypes.push(...eventVital.aliases);
      }

      const query = {
        period_days: range,
        event_types: eventTypes,
      };

      try {
        const response = await feathers
          .service(`reporting/daily-vitals/${endpoint}`)
          .find({ query });
        const mutation = getDailyVitalsMutation(endpoint);
        commit(mutation, response);
      } catch (error) {
        return commit('vitalsError', error);
      }
    },
  },
};
